import { TruncatedContent } from "@aurem/ui";
import { FileIcon, Paperclip } from "lucide-react";
import { useI18n } from "providers/I18nProvider";
import {
  gql,
  InternalAttachmentRunRecordOrderByColumn,
  InternalAttachmentRunRecordsFilterField,
  Operation,
} from "types";
import { formatFileSize } from "utils/helpers";
import { FieldType, InternalResourceConfig } from "../lib/internal-resources";

const GET_ATTACHMENT_RUN_RECORD_LIST = gql(/* GraphQL */ `
  query InternalAttachmentRunRecordList(
    $pagination: Pagination
    $filters: InternalAttachmentRunRecordsFiltersExpression
    $orderBy: InternalAttachmentRunRecordOrderByInput
    $search: String
  ) {
    internalAttachmentRunRecordList(
      pagination: $pagination
      filters: $filters
      orderBy: $orderBy
      search: $search
    ) {
      count
      list {
        id
        runId
        nodeId
        itemIndex
        entityType
        entityId
        entityDetails
        fileId
        fileUrl
        fileFilename
        fileSize
        createdAt
      }
    }
  }
`);

const renderItemIndex = (value: unknown) => {
  if (value === null || value === undefined) return "-";
  return String(value);
};

const renderFileFilename = (value: unknown) => {
  if (value === null || value === undefined) return "-";
  return String(value);
};

const AttachmentFileLink = ({ url }: { url: string }) => {
  const { t } = useI18n();

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t(
        "apps.admin.internalResources.attachmentRunRecord.openFile",
      )}
      className="text-neutral-500 hover:text-primary-600"
    >
      <FileIcon size={18} />
    </a>
  );
};

const renderFileLink = (value: unknown) => {
  if (typeof value !== "string" || value.length === 0) return "-";
  return <AttachmentFileLink url={value} />;
};

const renderEntityDetails = (value: unknown) => {
  if (typeof value !== "string" || value.length === 0) return "-";
  return <TruncatedContent>{value}</TruncatedContent>;
};

const renderFileSize = (value: unknown) => {
  if (value === null || value === undefined) return "-";
  const size = Number(value);
  if (Number.isNaN(size)) return "-";
  return formatFileSize(size);
};

export const attachmentRunRecordResourceConfig: InternalResourceConfig = {
  resourceName: "attachment-run-record",
  displayNameKey: "apps.admin.internalResources.attachmentRunRecord.title",
  basePath: "/internal/data/attachment-run-records",

  listQuery: GET_ATTACHMENT_RUN_RECORD_LIST,
  listQueryName: "internalAttachmentRunRecordList",

  fields: [
    {
      key: "runId",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.runId",
      type: FieldType.Uuid,
      sortKey: InternalAttachmentRunRecordOrderByColumn.RunId,
      width: 190,
    },
    {
      key: "nodeId",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.nodeId",
      type: FieldType.String,
      width: 140,
    },
    {
      key: "itemIndex",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.itemIndex",
      type: FieldType.String,
      format: renderItemIndex,
      width: 100,
    },
    {
      key: "entityType",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.entityType",
      type: FieldType.String,
      width: 160,
    },
    {
      key: "entityId",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.entityId",
      type: FieldType.Uuid,
      sortKey: InternalAttachmentRunRecordOrderByColumn.EntityId,
      width: 190,
    },
    {
      key: "entityDetails",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.entityDetails",
      type: FieldType.String,
      format: renderEntityDetails,
      width: 200,
    },
    {
      key: "fileId",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.fileId",
      type: FieldType.Uuid,
      width: 190,
    },
    {
      key: "fileFilename",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.fileFilename",
      type: FieldType.String,
      format: renderFileFilename,
      width: 220,
    },
    {
      key: "fileSize",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.fileSize",
      type: FieldType.String,
      format: renderFileSize,
      width: 120,
    },
    {
      key: "fileUrl",
      labelKey: "apps.admin.internalResources.attachmentRunRecord.columns.file",
      type: FieldType.String,
      format: renderFileLink,
      width: 80,
    },
    {
      key: "createdAt",
      labelKey:
        "apps.admin.internalResources.attachmentRunRecord.columns.createdAt",
      type: FieldType.DateTime,
      sortKey: InternalAttachmentRunRecordOrderByColumn.CreatedAt,
      width: 180,
    },
  ],

  list: {
    defaultSort: {
      column: InternalAttachmentRunRecordOrderByColumn.CreatedAt,
      direction: "desc",
    },
    filters: [
      {
        key: "runId",
        labelKey:
          "apps.admin.internalResources.attachmentRunRecord.filters.runId",
        placeholderKey:
          "apps.admin.internalResources.attachmentRunRecord.filters.runIdPlaceholder",
        field: InternalAttachmentRunRecordsFilterField.RunId,
        op: Operation.Eq,
        partial: {
          field: InternalAttachmentRunRecordsFilterField.RunIdText,
          op: Operation.Ilike,
        },
      },
    ],
    searchFields: [],
    searchArg: true,
  },

  navigation: {
    icon: <Paperclip size={16} />,
    order: 9,
  },
};
